import { useEffect, useCallback, useState, useRef } from 'react';

import { ConfluenceBackground } from '@/components/signup/confluence-background/src/confluence-background';
import { ProductKey } from '@/components/signup/utils/products';
import { UfoCustomTimingWrapper } from '@/components/signup/utils/ufo-custom-timing-wrapper';
import { ufoEventRedirectToProduct } from '@/components/signup/utils/ufo-events';
import { UfoCustomTimingKey } from '@/components/signup/utils/ufo.types';
import { useUserContext } from '@/components/user-context/user-context-provider';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { useProvisioningLatencyUfo } from '@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-provisioning-latency-ufo';

import { useCloudStatus } from '../../hooks/use-cloud-status';
import { useCloudStatusRedirection } from '../../hooks/use-cloud-status-redirection';

import { LoadingAnimation } from './loading-animation';

export const ConfluenceProvisioningUi = () => {
	const { triggerOnSuccess, startPerceivedProvisioningTimer, cloudId } = useCloudStatus();
	const { redirectUrl } = useCloudStatusRedirection();
	const { completeAll, startProductSetupSystem, completeProductSetupSystem } =
		useProvisioningLatencyUfo();
	const {
		profile: { cohorts },
	} = useUserContext();
	const stargateClient = useBxpWebStargateClient();
	const [isRovoActive, setIsRovoActive] = useState(false);
	const [attempts, setAttempts] = useState(0);
	const [isReadyToRedirect, setIsReadyToRedirect] = useState(false);
	const [outroAnimationComplete, setOutroAnimationComplete] = useState(false);
	const [outroStarted, setOutroStarted] = useState(false);
	const pendingRedirectUrl = useRef<string | null>(null);
	const MAX_RETRIES = 20;

	const scheduleRedirect = useCallback((url: string) => {
		pendingRedirectUrl.current = url;
		setIsReadyToRedirect(true);
	}, []);

	const callbackForRovoCheck = useCallback(async () => {
		if (cohorts?.domainType === 'business-domain' && cloudId && attempts < MAX_RETRIES) {
			// biz premium needs to check to see if rovo is done being provisioned
			const { products } = await stargateClient.getLicenceInformationStatus({ cloudId });
			const edition = products['confluence.ondemand']?.edition;
			if (edition === 'premium') {
				const rovoData = products['rovo.ondemand'];
				if (rovoData?.state === 'ACTIVE') {
					setIsRovoActive(true);
					scheduleRedirect(redirectUrl);
				}
			} else {
				// if biz but not premium just proceed to confluence
				scheduleRedirect(redirectUrl);
			}
		} else if (attempts === MAX_RETRIES) {
			// after max retries we want to proceed
			scheduleRedirect(redirectUrl);
		} else {
			// if not biz we want to proceed
			scheduleRedirect(redirectUrl);
		}
	}, [cohorts, stargateClient, redirectUrl, attempts, cloudId, scheduleRedirect]);

	const performRedirect = useCallback(() => {
		if (pendingRedirectUrl.current) {
			const url = pendingRedirectUrl.current;
			pendingRedirectUrl.current = null;
			ufoEventRedirectToProduct.success({
				customData: {
					reactComponent: 'ConfluenceLayout default',
					analyticsEvent: 'redirected',
					redirectFrom: 'confirmationPage',
					provisioningStatus: 'success',
				},
			});
			completeAll('confluence-provisioning-screen');
			completeProductSetupSystem('confluence-provisioning-screen');
			window.location.assign(url);
		}
	}, [completeAll, completeProductSetupSystem]);

	const handleOutroStarted = useCallback(() => {
		setOutroStarted(true);
	}, []);

	const handleOutroComplete = useCallback(() => {
		setOutroAnimationComplete(true);
		// Redirect immediately when the outro animation finishes.
		// Don't wait for a useEffect cycle — redirect synchronously.
		performRedirect();
	}, [performRedirect]);

	// Fallback: if provisioning completed before the outro animation started,
	// redirect once both conditions are met (handles race condition where
	// outroAnimationComplete was set before isReadyToRedirect).
	useEffect(() => {
		if (isReadyToRedirect && outroAnimationComplete && pendingRedirectUrl.current) {
			performRedirect();
		}
	}, [isReadyToRedirect, outroAnimationComplete, performRedirect]);

	// Redirect after the outro animation finishes playing (~2.5s).
	// The outro animation is ~2.4s long. This timer-based redirect is the
	// primary mechanism — it does not rely on the Lottie `complete` event
	// which can fail due to stale closures in react-lottie.
	// If pendingRedirectUrl is not yet set (Rovo check still pending),
	// the fallback useEffect above will redirect once both
	// outroAnimationComplete and isReadyToRedirect are true.
	useEffect(() => {
		if (!outroStarted) {
			return;
		}
		const outroRedirectDelay = setTimeout(() => {
			setOutroAnimationComplete(true);
			performRedirect();
		}, 2500);
		return () => clearTimeout(outroRedirectDelay);
	}, [outroStarted, performRedirect]);

	useEffect(() => {
		startPerceivedProvisioningTimer();
	}, [startPerceivedProvisioningTimer]);

	useEffect(() => {
		// check for rovo done every 3 seconds for max of 5 times before redirecting after success signa;
		if (!isRovoActive && attempts <= MAX_RETRIES && redirectUrl && triggerOnSuccess) {
			if (attempts === 0) {
				startProductSetupSystem({ productKey: ProductKey.CONFLUENCE, provisioningFlow: 'regular' });
			}
			callbackForRovoCheck();
			const timer = setTimeout(() => {
				setAttempts((prev) => prev + 1);
			}, 3000);
			return () => clearTimeout(timer);
		}
	}, [
		isRovoActive,
		attempts,
		redirectUrl,
		callbackForRovoCheck,
		triggerOnSuccess,
		startProductSetupSystem,
	]);

	return (
		<div data-testid="confluence-provisioning-ui">
			<UfoCustomTimingWrapper
				ufoEvent={ufoEventRedirectToProduct}
				ufoCustomTimingKey={UfoCustomTimingKey.CONFLUENCE_ONBOARDING_SPINNER_DWELL_TIME}
			>
				<ConfluenceBackground />
				<LoadingAnimation
					isLoadingComplete={isReadyToRedirect}
					onOutroStarted={handleOutroStarted}
					onOutroComplete={handleOutroComplete}
				/>
			</UfoCustomTimingWrapper>
		</div>
	);
};
